import React from "react";

import "../styles.scss";
import "../scripts";

import {
  KameleonElement,
  primaryCollectionBlock,
  getDefaultProps,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";
import { webflowHOC } from "@kameleon-common/webflow-js";
import {
  backgroundColorModeMapper,
  BackgroundColors as ThemeBackgroundColors,
  Variants,
} from "@kameleon-util/global-theme";
import {
  blockConfig,
  ContentCollectionItem,
  ContentCollectionItemType,
} from "./components/content-collection-item";

import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import {
  ContentCollectionType as ComponentType,
  layoutOptions,
} from "./types/prop-types";

type ContentCollectionType = {
  PrimaryBlocks: ContentCollectionItemType[];
} & ComponentType;

const Component = ({
  PrimaryBlocks,
  tagline,
  header,
  description,
  layoutProps,
}: ContentCollectionType) => {
  const primaryBlockLayoutProps = { ...layoutProps };
  primaryBlockLayoutProps.backgroundColor = ThemeBackgroundColors.neutralLight;

  return (
    <Box className="primary-collection-content-collection-two-col-tabs-rows-v1">
      <Box className="padding-global">
        <Box className="container-large">
          <Box className="padding-section-large">
            <Box className="w-layout-grid career21_component">
              <Box className="career21_content-container">
                {tagline && (
                  <Box className="margin-bottom margin-xsmall">
                    <Variants.TagLine>{tagline}</Variants.TagLine>
                  </Box>
                )}
                <Box className="margin-bottom margin-small">
                  <Variants.Heading>{header}</Variants.Heading>
                </Box>
                <Variants.ParagraphMedium>
                  {description}
                </Variants.ParagraphMedium>
              </Box>
              <Box
                data-current="Tab 1"
                data-easing="ease"
                data-duration-in="300"
                data-duration-out="100"
                className="career21_tabs w-tabs"
              >
                <Box className="career21_tabs-menu no-scrollbar w-tab-menu">
                  {PrimaryBlocks.map((primary, i) => (
                    <Variants.LinkTab
                      key={`tab-${i}`}
                      data-w-tab={`Tab ${i + 1}`}
                      className="career21_tab-link w-inline-block w-tab-link w--current"
                    >
                      <Variants.ParagraphDefault>
                        {primary.tab}
                      </Variants.ParagraphDefault>
                    </Variants.LinkTab>
                  ))}
                </Box>
                <Box className="w-tab-content">
                  {PrimaryBlocks?.map((primaryBlockProps, index) => {
                    const primaryProps = { ...primaryBlockProps };
                    primaryProps.layoutProps = primaryBlockLayoutProps;
                    primaryProps._parentBgColor = backgroundColorModeMapper(
                      layoutProps.backgroundColor
                    ).color;

                    return (
                      <Box
                        className="career21_tab-pane w-tab-pane w--tab-active"
                        key={`item-${index}`}
                        data-w-tab={`Tab ${index ? index + 1 : 1}`}
                      >
                        <ContentCollectionItem {...primaryProps} />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ComponentWithWebflow = webflowHOC(Component, ["tabs"]);

export const ContentCollection = primaryCollectionBlock<ContentCollectionType>(
  blockConfig,
  ({ PrimaryBlocks, ...props }: ContentCollectionType) => (
    <ComponentWithWebflow PrimaryBlocks={PrimaryBlocks} {...props} />
  )
);

ContentCollection.defaultProps =
  getDefaultProps<ContentCollectionType>(defaults);

export const Element: KameleonElement = {
  component: ContentCollection,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
